import { makeAutoObservable, runInAction } from 'mobx';

import api from '@/api/index';
import type {
    AdTagBatchResult,
    AdTagDomainItem,
    AdTagDomainsMeta,
    AdTagImportPreview,
    AdTagImportResult,
    AdTagInvalidDomain,
} from '@/types/adtag';
import {
    IMPORT_CHUNK_SIZE,
    countDuplicatesInInput,
    uniqueDomains,
} from '@/utils/parseDomainList';

const EMPTY_META: AdTagDomainsMeta = { total: 0, page: 1, per_page: 100, last_page: 1 };

/** Сколько примеров ошибок храним и показываем */
const INVALID_SAMPLE_LIMIT = 100;

/**
 * Состояние экрана управления доменами группы ad_tag (T-10).
 *
 * Список всегда пагинированный: доменов может быть 100 000, отдавать их
 * целиком нельзя ни серверу, ни браузеру.
 */
class StoreAdTagDomains {
    groupId: number | null = null;

    // ── список ────────────────────────────────────────────────
    items: AdTagDomainItem[] = [];
    meta: AdTagDomainsMeta = { ...EMPTY_META };
    search = '';
    activeFilter: boolean | null = null;
    listLoading = false;
    listError: string | null = null;

    // ── выделение ─────────────────────────────────────────────
    selectedIds: number[] = [];

    // ── импорт ────────────────────────────────────────────────
    preview: AdTagImportPreview | null = null;
    previewLoading = false;
    importing = false;
    processed = 0;
    queued = 0;
    importResult: AdTagImportResult | null = null;
    importError: string | null = null;

    constructor() {
        makeAutoObservable(this);
    }

    // ── список ────────────────────────────────────────────────

    /**
     * Привязать стор к группе без загрузки списка.
     *
     * Нужно диалогу импорта: он работает с той же группой, но список доменов
     * ему не нужен. Без этого `previewDomains`/`importDomains` уходили в
     * no-op — groupId оставался null (найдено E2E-тестом).
     */
    setGroup(groupId: number) {
        if (this.groupId === groupId) {
            return;
        }

        this.groupId = groupId;
        this.resetImport();
    }

    async open(groupId: number) {
        this.groupId = groupId;
        this.items = [];
        this.meta = { ...EMPTY_META };
        this.search = '';
        this.activeFilter = null;
        this.selectedIds = [];
        this.resetImport();

        await this.load();
    }

    close() {
        this.groupId = null;
        this.items = [];
        this.selectedIds = [];
        this.resetImport();
    }

    setSearch(value: string) {
        this.search = value;
        this.meta.page = 1;
        void this.load();
    }

    setActiveFilter(value: boolean | null) {
        this.activeFilter = value;
        this.meta.page = 1;
        void this.load();
    }

    setPage(page: number) {
        if (page < 1 || page > this.meta.last_page) {
            return;
        }
        this.meta.page = page;
        void this.load();
    }

    async load() {
        if (this.groupId === null) {
            return;
        }

        this.listLoading = true;
        this.listError = null;

        try {
            const { data } = await api.adtag.getDomains(this.groupId, {
                page: this.meta.page,
                per_page: this.meta.per_page,
                ...(this.search ? { search: this.search } : {}),
                ...(this.activeFilter === null ? {} : { is_active: this.activeFilter ? 1 : 0 }),
            });

            runInAction(() => {
                this.items = data?.data?.items ?? [];
                this.meta = data?.data?.meta ?? { ...EMPTY_META };
            });
        } catch (error) {
            runInAction(() => {
                // Прежние данные сохраняем — экран не должен «мигать» пустотой
                this.listError = 'Не удалось загрузить домены';
            });
        } finally {
            runInAction(() => {
                this.listLoading = false;
            });
        }
    }

    async toggleActive(item: AdTagDomainItem) {
        if (this.groupId === null) {
            return;
        }

        const nextValue = !item.is_active;

        try {
            await api.adtag.updateDomain(this.groupId, item.id, {
                domain: item.domain,
                is_active: nextValue,
            });

            runInAction(() => {
                const target = this.items.find((i) => i.id === item.id);
                if (target) {
                    target.is_active = nextValue;
                }
            });
        } catch (error) {
            runInAction(() => {
                this.listError = 'Не удалось изменить статус домена';
            });
        }
    }

    toggleSelected(id: number) {
        this.selectedIds = this.selectedIds.includes(id)
            ? this.selectedIds.filter((selected) => selected !== id)
            : [...this.selectedIds, id];
    }

    clearSelection() {
        this.selectedIds = [];
    }

    async removeSelected() {
        if (this.groupId === null || this.selectedIds.length === 0) {
            return;
        }

        try {
            await api.adtag.batchDeleteDomains(this.groupId, this.selectedIds);

            runInAction(() => {
                this.selectedIds = [];
            });

            await this.load();
        } catch (error) {
            runInAction(() => {
                this.listError = 'Не удалось удалить выбранные домены';
            });
        }
    }

    // ── импорт ────────────────────────────────────────────────

    /**
     * Предпросмотр (FR-1.2): dry_run-прогон по чанкам + подсчёт повторов
     * внутри ввода на клиенте. Ничего не пишет.
     */
    async previewDomains(rawDomains: string[]) {
        if (this.groupId === null || rawDomains.length === 0) {
            return;
        }

        const duplicatesInInput = countDuplicatesInInput(rawDomains);
        const domains = uniqueDomains(rawDomains);

        this.previewLoading = true;
        this.importError = null;
        this.preview = null;

        try {
            const aggregate: AdTagImportPreview = {
                total: rawDomains.length,
                duplicatesInInput,
                valid: 0,
                existing: 0,
                invalidCount: 0,
                invalid: [],
            };

            for (const chunk of this.chunk(domains)) {
                const result = await this.sendChunk(chunk, true);
                this.accumulatePreview(aggregate, result);
            }

            runInAction(() => {
                this.preview = aggregate;
            });
        } catch (error) {
            runInAction(() => {
                this.importError = 'Не удалось проверить список';
            });
        } finally {
            runInAction(() => {
                this.previewLoading = false;
            });
        }
    }

    /**
     * Импорт (FR-1.3): чанки по 5000 с прогрессом. Операция идемпотентна,
     * поэтому повтор после сбоя безопасен.
     */
    async importDomains(rawDomains: string[]) {
        if (this.groupId === null || rawDomains.length === 0) {
            return;
        }

        const domains = uniqueDomains(rawDomains);
        const chunks = this.chunk(domains);

        this.importing = true;
        this.importError = null;
        this.processed = 0;
        this.queued = rawDomains.length;
        this.importResult = {
            created: 0,
            skippedExisting: 0,
            invalidCount: 0,
            invalid: [],
        };

        try {
            for (const chunk of chunks) {
                const result = await this.sendChunk(chunk, false);

                runInAction(() => {
                    const current = this.importResult ?? {
                        created: 0, skippedExisting: 0, invalidCount: 0, invalid: [],
                    };

                    this.importResult = {
                        created: current.created + result.created,
                        skippedExisting: current.skippedExisting + result.skipped_existing,
                        invalidCount: current.invalidCount + result.invalid_count,
                        invalid: this.mergeInvalid(current.invalid, result.invalid),
                    };
                    this.processed += chunk.length;
                });
            }
        } catch (error) {
            runInAction(() => {
                this.importError = 'Импорт прерван. Уже добавленные домены сохранены — можно повторить.';
            });
        } finally {
            runInAction(() => {
                this.importing = false;
            });

            await this.load();
        }
    }

    resetImport() {
        this.preview = null;
        this.previewLoading = false;
        this.importing = false;
        this.processed = 0;
        this.queued = 0;
        this.importResult = null;
        this.importError = null;
    }

    get importProgressPercent(): number {
        if (this.queued === 0) {
            return 0;
        }

        return Math.min(100, Math.round((this.processed / this.queued) * 100));
    }

    private chunk(domains: string[]): string[][] {
        const chunks: string[][] = [];

        for (let i = 0; i < domains.length; i += IMPORT_CHUNK_SIZE) {
            chunks.push(domains.slice(i, i + IMPORT_CHUNK_SIZE));
        }

        return chunks;
    }

    private async sendChunk(chunk: string[], dryRun: boolean): Promise<AdTagBatchResult> {
        const { data } = await api.adtag.batchDomains(this.groupId, {
            domains: chunk,
            dry_run: dryRun,
            skip_existing: true,
        });

        return data?.data as AdTagBatchResult;
    }

    private accumulatePreview(aggregate: AdTagImportPreview, result: AdTagBatchResult) {
        aggregate.valid += result.created;
        aggregate.existing += result.skipped_existing;
        aggregate.invalidCount += result.invalid_count;
        aggregate.invalid = this.mergeInvalid(aggregate.invalid, result.invalid);
    }

    private mergeInvalid(
        current: AdTagInvalidDomain[],
        added: AdTagInvalidDomain[],
    ): AdTagInvalidDomain[] {
        if (current.length >= INVALID_SAMPLE_LIMIT) {
            return current;
        }

        return current.concat(added).slice(0, INVALID_SAMPLE_LIMIT);
    }
}

export const storeAdTagDomains = new StoreAdTagDomains();
export default StoreAdTagDomains;
